import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";
import { toast } from "sonner";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", organization: "", email: "", password: "", partner_code: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(form);
      toast.success(`Compte créé, bienvenue ${user.name} !`);
      navigate("/partner");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 fade-in">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-sage flex items-center justify-center text-white mb-4">
            <Calendar className="w-7 h-7" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-ink">Devenir partenaire</h1>
          <p className="text-sm text-muted_text mt-2">
            Mairies et commerçants : publiez vos événements locaux.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white rounded-2xl border border-[#F0E0CC] p-6 sm:p-8 space-y-5"
          data-testid="register-form"
        >
          <div className="space-y-2">
            <Label htmlFor="name" className="text-ink font-medium">Nom complet *</Label>
            <Input id="name" required minLength={2} value={form.name} onChange={update("name")}
              className="h-11 rounded-xl border-[#F0E0CC] focus-visible:ring-sage/20 focus-visible:border-sage"
              data-testid="register-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="organization" className="text-ink font-medium">Mairie / Commerce</Label>
            <Input id="organization" value={form.organization} onChange={update("organization")}
              placeholder="Ex : Mairie de Saint-Jean"
              className="h-11 rounded-xl border-[#F0E0CC] focus-visible:ring-sage/20 focus-visible:border-sage"
              data-testid="register-organization-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-ink font-medium">Email *</Label>
            <Input id="email" type="email" required value={form.email} onChange={update("email")}
              className="h-11 rounded-xl border-[#F0E0CC] focus-visible:ring-sage/20 focus-visible:border-sage"
              data-testid="register-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-ink font-medium">Mot de passe * <span className="text-xs text-muted_text">(min. 6 caractères)</span></Label>
            <Input id="password" type="password" required minLength={6} value={form.password} onChange={update("password")}
              className="h-11 rounded-xl border-[#F0E0CC] focus-visible:ring-sage/20 focus-visible:border-sage"
              data-testid="register-password-input" />
          </div>

          <Button type="submit" disabled={loading}
            className="w-full h-11 rounded-xl bg-sage hover:bg-sage-dark text-white font-medium"
            data-testid="register-submit-button">
            {loading ? "Création…" : "Créer mon compte"}
          </Button>

          <div className="space-y-2">
            <Label htmlFor="partner_code" className="text-ink font-medium">Code partenaire *</Label>
            <Input
              id="partner_code"
              type="text"
              required
              minLength={4}
              value={form.partner_code}
              onChange={(e) => setForm({ ...form, partner_code: e.target.value.toUpperCase() })}
              placeholder="EPI-XXXXXXXX"
              className="h-11 rounded-xl border-[#F0E0CC] focus-visible:ring-sage/20 focus-visible:border-sage font-mono tracking-wider"
              data-testid="register-partner-code-input"
            />
          </div>

          <div className="rounded-xl bg-[#FEF3C7] border border-[#FDE68A] p-4 text-sm text-ink" data-testid="partner-code-info">
            <p className="font-medium mb-1">Code partenaire requis</p>
            <p className="text-muted_text leading-relaxed">
              Pour finaliser votre inscription, saisissez le code partenaire qui vous a été
              envoyé par email après votre demande de partenariat. Si vous n'avez pas encore de code,
              contactez-nous à <span className="text-sage font-medium">epicentre56@gmail.com</span>.
            </p>
          </div>

          <p className="text-center text-sm text-muted_text">
            Déjà un compte ?{" "}
            <Link to="/login" className="text-sage font-medium hover:underline" data-testid="link-to-login">
              Se connecter
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
